import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { SignIn } from '../components/auth/SignIn';
import { SignUp } from '../components/auth/SignUp';
import { SplashPage } from '../components/SplashPage';
import { PrivacyPage } from '../components/PrivacyPage';
import { AppLayout } from '../components/layout/AppLayout';
import { App } from '../App';
import { SessionEditor } from '../pages/SessionEditor';
import { Dashboard } from '../pages/Dashboard';
import { Sessions } from '../pages/Sessions';
import { SessionDetail } from '../pages/SessionDetail';
import { RehearsalHistory } from '../pages/RehearsalHistory';
import { OrgSessions } from '../pages/OrgSessions';
import { OrgDashboard } from '../pages/OrgDashboard';
import { Templates } from '../pages/Templates';
import { Teams } from '../pages/Teams';
import { TeamDetail } from '../pages/TeamDetail';
import { Settings } from '../pages/Settings';
import { OrgVocabulary } from '../pages/OrgVocabulary';
import { OrgDocumentation } from '../pages/OrgDocumentation';
import { OrgMembers } from '../pages/OrgMembers';
import { PrivacySettings } from '../pages/PrivacySettings';
import { SessionPopup } from '../pages/SessionPopup';

/**
 * Protected route wrapper - redirects to sign-in if not authenticated
 */
function ProtectedRoute() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <Outlet />;
}

/**
 * Public route wrapper for auth pages - redirects to dashboard if already authenticated
 */
function PublicOnlyRoute() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

export const router = createBrowserRouter([
  // Public landing page (accessible to everyone)
  {
    path: '/',
    element: <SplashPage />,
  },

  // Privacy policy (public)
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },

  // Auth routes (redirect to dashboard if already signed in)
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        path: '/sign-in/*',
        element: <SignIn />,
      },
      {
        path: '/sign-up/*',
        element: <SignUp />,
      },
    ],
  },

  // Protected popup routes (no layout)
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/session-popup',
        element: <SessionPopup />,
      },
    ],
  },

  // Protected routes with app layout
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: '/dashboard',
            element: <Dashboard />,
          },
          {
            path: '/app',
            element: <SessionEditor />,
          },
          {
            // Legacy route - old App with floating widget
            // To revert: swap SessionEditor and App above
            path: '/app-legacy',
            element: <App />,
          },
          {
            path: '/sessions',
            element: <Sessions />,
          },
          {
            path: '/sessions/:id',
            element: <SessionDetail />,
          },
          {
            path: '/rehearsals',
            element: <RehearsalHistory />,
          },
          {
            path: '/templates',
            element: <Templates />,
          },
          {
            path: '/settings/teams',
            element: <Teams />,
          },
          {
            path: '/settings/teams/:teamId',
            element: <TeamDetail />,
          },
          {
            path: '/org/dashboard',
            element: <OrgDashboard />,
          },
          {
            path: '/org/sessions',
            element: <OrgSessions />,
          },
          {
            path: '/settings',
            element: <Settings />,
          },
          {
            path: '/settings/billing',
            element: <Settings />,
          },
          {
            path: '/settings/privacy',
            element: <PrivacySettings />,
          },
          {
            path: '/org/vocabulary',
            element: <OrgVocabulary />,
          },
          {
            path: '/org/documentation',
            element: <OrgDocumentation />,
          },
          {
            path: '/settings/members',
            element: <OrgMembers />,
          },
        ],
      },
    ],
  },

  // Catch-all redirect to home
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
